import { getSocialPlatformSettings } from "@/fixtures/socialPlatforms";
import {
  EllipsisOutlined,
  GlobalOutlined,
  LikeFilled,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
import { useMemo } from "react";
import { BiCommentDetail, BiLike } from "react-icons/bi";
import { FaRetweet } from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import CaptionExpand from "./components/CaptionExpand";
import MediaSection from "./components/MediaSection";
import { BaseSocialCardProps } from "./types";

export interface LinkedinCardProps extends BaseSocialCardProps {
  platformId: string;
}

const LinkedinCard = ({
  caption,
  mediaUrls = [],
  username = "User Name",
  profileUrl,
  postedAt: _postedAt,
  platformId,
  isTemplateVideo,
}: LinkedinCardProps) => {
  const captionLimit = useMemo(() => {
    const settings = getSocialPlatformSettings(platformId);
    return settings?.limits?.caption;
  }, [platformId]);

  const displayCaption = useMemo(() => {
    if (captionLimit && caption.length > captionLimit) {
      return caption.substring(0, captionLimit) + "...";
    }
    return caption;
  }, [caption, captionLimit]);

  return (
    <div className="w-full overflow-hidden shadow-md border rounded-lg border-antd-colorBorderSecondary bg-antd-colorBgContainer text-antd-colorText linkedin-card-preview">
      <div className="py-3 flex justify-between items-center">
        <div className="flex items-center gap-2 pl-4">
          <Avatar
            size={40}
            src={profileUrl}
            icon={!profileUrl ? <UserOutlined /> : undefined}
          />
          <div className="flex flex-col">
            <span className="font-semibold text-sm">{username}</span>
            <div className="flex items-center text-xs text-antd-colorTextSecondary">
              <span>Just now</span>
              <span className="mx-1">•</span>
              <GlobalOutlined />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 pr-4">
          <div className="text-antd-colorTextSecondary">
            <EllipsisOutlined />
          </div>
        </div>
      </div>

      {displayCaption && (
        <div className="px-4 pb-2">
          <CaptionExpand
            text={displayCaption}
            initialLines={4}
            showMoreText="...see more"
            showLessText="...less"
            linkColor="gray"
            className="text-sm text-antd-colorText"
          />
        </div>
      )}

      <div className="flex justify-center max-h-[450px] min-h-[200px]">
        <MediaSection mediaUrls={mediaUrls} isTemplateVideo={isTemplateVideo} />
      </div>

      <div className="px-4 py-1 flex justify-between items-center text-antd-colorTextSecondary text-sm mt-2">
        <div className="flex items-center gap-1">
          <div className="flex">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 border-2 border-white">
              <LikeFilled style={{ fontSize: "8px", color: "white" }} />
            </span>
          </div>
          <span>29</span>
        </div>
        <div className="flex gap-2">
          <span>7 comments</span>
        </div>
      </div>

      <div className="py-1 flex justify-around items-center border-t border-antd-colorBorderSecondary mt-1">
        <div className="flex flex-1 flex-col items-center justify-center h-auto p-2 text-antd-colorTextSecondary text-xs">
          <BiLike className="mb-1" style={{ fontSize: "18px" }} />
          Like
        </div>
        <div className="flex flex-1 flex-col items-center justify-center h-auto p-2 text-antd-colorTextSecondary text-xs">
          <BiCommentDetail className="mb-1" style={{ fontSize: "18px" }} />
          Comment
        </div>
        <div className="flex flex-1 flex-col items-center justify-center h-auto p-2 text-antd-colorTextSecondary text-xs">
          <FaRetweet className="mb-1" style={{ fontSize: "18px" }} />
          Repost
        </div>
        <div className="flex flex-1 flex-col items-center justify-center h-auto p-2 text-antd-colorTextSecondary text-xs">
          <FiSend className="mb-1" style={{ fontSize: "18px" }} />
          Send
        </div>
      </div>
    </div>
  );
};

export default LinkedinCard;
